import { useId, type ReactNode } from 'react'

import { Okvir } from '../Field'
import { Okvir as OkvirNoir } from '../noir'
import { MJERA_KRUPNO, MJERA_POLJA } from '../polje'

/**
 * DVA SUČELJA, JEDAN PICKER (D15-A §4).
 *
 * Polja datuma stoje i na konzoli (paper/ink) i na noir površinama osoblja —
 * "Novi sastanak", protuprijedlog na upit. Račun kalendara, 24-satni sat i oblik
 * `d.M.yyyy` su ondje isti do zadnjeg znaka; razlikuju se samo boje i okvir koji
 * ekran već nosi.
 *
 * Zato picker prima `ton`, a ovdje stoji SVE po čemu se dva tona razlikuju — na
 * jednom mjestu, umjesto uvjeta razasutih po tri komponente.
 */
export type Ton = 'konzola' | 'noir'

/** Boje kontrole; mjere dolaze iz `components/polje.ts` i iste su za oba tona. */
export function razredPolja(ton: Ton, krupno: boolean, greska?: string): string {
  const mjera = krupno ? MJERA_KRUPNO : MJERA_POLJA

  if (ton === 'noir') {
    return `${mjera} bg-noir text-ivory focus:outline-none ${
      greska ? 'border-champagne' : 'border-line focus:border-brass'
    }`
  }

  return `${mjera} bg-card text-ink focus:border-brass focus:outline-none ${
    greska ? 'border-bad-tx' : 'border-hair'
  }`
}

/** Podloga i rub kalendara koji se otvara ispod polja. */
export function razredKalendara(ton: Ton): string {
  return ton === 'noir'
    ? 'mt-2 rounded-xl border border-line bg-surface p-3'
    : 'mt-2 rounded-xl border border-hair bg-paper p-3'
}

/** Kako se u tom tonu crta dan koji NIJE izabran. */
export function razredDana(ton: Ton, izabran: boolean, danasnji: boolean): string {
  if (izabran) {
    return ton === 'noir' ? 'bg-brass font-semibold text-noir' : 'bg-brass font-semibold text-noir'
  }

  if (danasnji) return ton === 'noir' ? 'border border-brass text-ivory' : 'border border-brass text-ink'

  return ton === 'noir' ? 'text-ivory hover:bg-noir' : 'text-ink hover:bg-card'
}

/** Prigušen tekst (placeholder polja, imena dana, "Očisti"). */
export function razredPrigusen(ton: Ton): string {
  return ton === 'noir' ? 'text-stone' : 'text-ink2'
}

/**
 * Okvir koji ekran već nosi. Oba imaju istu djecu — `(id, opisano) => …` — pa se
 * picker ispod ovoga ne mora granati.
 */
export function OkvirTona({
  ton,
  label,
  hint,
  greska,
  obavezno,
  krupno,
  natpisSkriven = false,
  children,
}: {
  ton: Ton
  label: string
  hint?: string
  greska?: string
  obavezno?: boolean
  krupno?: boolean
  /**
   * Natpis postoji, ali se ne VIDI. Jedini korisnik je sat u paru s danom
   * (`PoljeDatumaVremena`): ime mu mora biti jedinstveno — dvije kontrole zvane
   * "Vrijeme" na istom ekranu ne razlikuje ni čitač ekrana ni test — a ponoviti
   * puni natpis nad polovicom jedne odluke bilo bi dvaput isto pitanje.
   */
  natpisSkriven?: boolean
  children: (id: string, opisano: string | undefined) => ReactNode
}) {
  const vlastitiId = useId()

  if (natpisSkriven) {
    const idHint = hint ? `${vlastitiId}-hint` : undefined

    return (
      <div className="flex flex-col gap-1.5">
        <label htmlFor={vlastitiId} className="sr-only">
          {label}
        </label>

        {children(vlastitiId, idHint)}

        {hint ? (
          <p id={idHint} className={`text-xs ${razredPrigusen(ton)}`}>
            {hint}
          </p>
        ) : null}
      </div>
    )
  }

  if (ton === 'noir') {
    return (
      <OkvirNoir oznaka={label} hint={hint} greska={greska} krupno={krupno}>
        {children}
      </OkvirNoir>
    )
  }

  return (
    <Okvir label={label} hint={hint} greska={greska} obavezno={obavezno}>
      {children}
    </Okvir>
  )
}
